import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';
import {
  EdgeFunctionName,
  EnvVarInfo,
  ManageEnvVarsParams,
} from 'types/edgeFunctions';
import { ERoute } from 'types/frontend';

import { TEntityType } from '../../types';

const ENV_VARS_SNIPPET = `// 1. Add a variable above:
//    KEY_NAME → BASE_URL
//    Value   → https://jsonplaceholder.typicode.com
//
// 2. Use {{BASE_URL}} — replaced server-side:
async function fetchPosts() {
  const res = await bds.api(
    '{{BASE_URL}}/posts?_limit=5'
  );
  const posts = await res.json();
  console.log(posts);
}

fetchPosts();`;

interface IEnvVarsEditorTabProps {
  entityType?: TEntityType;
  entityId?: string;
}

const EnvVarsEditorTab = ({ entityType, entityId }: IEnvVarsEditorTabProps) => {
  const supabase = useSupabaseTypedClient();
  const { toastSuccess, toastError } = useToastWrapper();
  const { isFree, isTerminated } = useCurrentUser();

  const [vars, setVars] = useState<EnvVarInfo[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showNewValue, setShowNewValue] = useState(false);
  const [editingVar, setEditingVar] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showEditValue, setShowEditValue] = useState(false);

  const envVarMutation = useMutation<
    EnvVarInfo | EnvVarInfo[] | { success: boolean },
    string,
    ManageEnvVarsParams
  >({
    mutationFn: supabaseMutationFn((params) =>
      supabase.functions.invoke(EdgeFunctionName.ManageEnvVars, {
        body: params,
      })
    ),
  });

  const isPremium = !isFree && !isTerminated;
  const hasEntityContext = entityType && entityId;

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toastSuccess('Copied to clipboard');
    } catch {
      toastError('Failed to copy');
    }
  };

  const loadVars = useCallback(() => {
    if (!entityType || !entityId) return;
    envVarMutation.mutate(
      { type: 'list', entityType, entityId },
      {
        onSuccess: (data) => {
          setVars(data as EnvVarInfo[]);
          setIsLoaded(true);
        },
        onError: () => toastError('Failed to load variables'),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  useEffect(() => {
    if (isPremium && hasEntityContext) {
      loadVars();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const handleCreate = () => {
    if (!entityType || !entityId) return;
    if (!newKeyName.trim() || !newValue.trim()) {
      toastError('Both key name and value are required');
      return;
    }

    envVarMutation.mutate(
      { type: 'create', entityType, entityId, keyName: newKeyName.trim(), value: newValue },
      {
        onSuccess: () => {
          toastSuccess('Variable created');
          setNewKeyName('');
          setNewValue('');
          loadVars();
        },
        onError: (error) => toastError(String(error)),
      }
    );
  };

  const handleUpdate = (keyName: string) => {
    if (!entityType || !entityId) return;
    if (!editValue.trim()) {
      toastError('Value cannot be empty');
      return;
    }

    envVarMutation.mutate(
      { type: 'update', entityType, entityId, keyName, value: editValue },
      {
        onSuccess: () => {
          toastSuccess('Variable updated');
          setEditingVar(null);
          setEditValue('');
          loadVars();
        },
        onError: (error) => toastError(String(error)),
      }
    );
  };

  const handleDelete = (keyName: string) => {
    if (!entityType || !entityId) return;

    envVarMutation.mutate(
      { type: 'delete', entityType, entityId, keyName },
      {
        onSuccess: () => {
          toastSuccess('Variable deleted');
          loadVars();
        },
        onError: (error) => toastError(String(error)),
      }
    );
  };

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Text fontSize="large" fontWeight="semibold" mb={1}>
          Environment Variables
        </Text>
        <Text fontSize="small" opacity="secondary">
          Encrypted secrets scoped to this entity. Use{' '}
          <Box as="code" fontSize="xs" bg="whiteAlpha.200" px={1} borderRadius="sm">
            {'{{VAR_NAME}}'}
          </Box>{' '}
          in your{' '}
          <Box as="code" fontSize="xs" bg="whiteAlpha.200" px={1} borderRadius="sm">
            bds.api()
          </Box>{' '}
          calls to inject them server-side.
        </Text>
      </Box>

      {!isPremium ? (
        <>
          {/* Add Variable Form — visible but disabled */}
          <Box p={3} bg="whiteAlpha.100" borderRadius="md" opacity={0.5} pointerEvents="none">
            <Text fontSize="small" fontWeight="semibold" mb={3}>
              Add Variable
            </Text>
            <VStack align="stretch" spacing={2}>
              <Input
                placeholder="KEY_NAME (e.g. API_KEY)"
                size="sm"
                fontFamily="mono"
                fontSize="sm"
                bg="blackAlpha.400"
                isDisabled
              />
              <Input
                placeholder="Value"
                type="password"
                size="sm"
                fontFamily="mono"
                fontSize="sm"
                bg="blackAlpha.400"
                isDisabled
              />
              <Button scheme="primary" size="sm" isDisabled>
                Add Variable
              </Button>
            </VStack>
          </Box>

          {/* Inline upgrade CTA */}
          <Box
            p={3}
            borderRadius="md"
            bgGradient="linear(to-r, purple.900, blue.900)"
            borderWidth="1px"
            borderColor="purple.600"
          >
            <Text fontSize="small" fontWeight="semibold" mb={1}>
              Encrypted environment variables
            </Text>
            <Text fontSize="micro" opacity="secondary" mb={3}>
              Store API keys and secrets scoped to each project. Injected server-side via{' '}
              <Box as="code" fontSize="xs" bg="whiteAlpha.200" px={1} borderRadius="sm">
                {'{{VAR_NAME}}'}
              </Box>{' '}
              — never exposed in the browser.
            </Text>
            <Button scheme="gradient" size="sm" to={ERoute.Plans} leftIcon={<Icon type="Rocket" />} width="100%">
              Upgrade to BIG to use env variables
            </Button>
          </Box>

          {/* Usage Info — always visible */}
          <Box p={3} bg="whiteAlpha.50" borderRadius="md">
            <Text fontSize="micro" opacity="tertiary">
              Variables are encrypted server-side with AES-256-GCM. They are never exposed in the
              browser. Max 20 variables per entity, values up to 4096 characters.
            </Text>
          </Box>
        </>
      ) : !hasEntityContext ? (
        <Text fontSize="small" opacity="secondary">
          Environment variables are not available in this context.
        </Text>
      ) : (
        <>
          {/* Add Variable Form */}
          <Box p={3} bg="whiteAlpha.100" borderRadius="md">
            <Text fontSize="small" fontWeight="semibold" mb={3}>
              Add Variable
            </Text>
            <VStack align="stretch" spacing={2}>
              <Input
                placeholder="KEY_NAME (e.g. API_KEY)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                size="sm"
                fontFamily="mono"
                fontSize="sm"
                bg="blackAlpha.400"
              />
              <InputGroup size="sm">
                <Input
                  placeholder="Value"
                  type={showNewValue ? 'text' : 'password'}
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  fontFamily="mono"
                  fontSize="sm"
                  bg="blackAlpha.400"
                  pr="2.5rem"
                />
                <InputRightElement width="2.5rem">
                  <IconButton
                    aria-label={showNewValue ? 'Hide value' : 'Show value'}
                    icon={showNewValue ? <ViewOffIcon /> : <ViewIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={() => setShowNewValue(!showNewValue)}
                  />
                </InputRightElement>
              </InputGroup>
              <Button
                scheme="primary"
                size="sm"
                onClick={handleCreate}
                isLoading={envVarMutation.isPending}
                isDisabled={!newKeyName.trim() || !newValue.trim()}
              >
                Add Variable
              </Button>
            </VStack>
          </Box>

          {/* Variable List */}
          {!isLoaded ? (
            <Spinner size="sm" />
          ) : vars.length === 0 ? (
            <Text fontSize="small" opacity="secondary">
              No variables yet. Add one above.
            </Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {vars.map((v) => (
                <Box
                  key={v.id}
                  p={3}
                  bg="whiteAlpha.100"
                  borderRadius="md"
                >
                  {editingVar === v.keyName ? (
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="small" fontFamily="mono" fontWeight="semibold">
                        {v.keyName}
                      </Text>
                      <InputGroup size="sm">
                        <Input
                          placeholder="New value"
                          type={showEditValue ? 'text' : 'password'}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          fontFamily="mono"
                          fontSize="sm"
                          bg="blackAlpha.400"
                          pr="2.5rem"
                        />
                        <InputRightElement width="2.5rem">
                          <IconButton
                            aria-label={showEditValue ? 'Hide value' : 'Show value'}
                            icon={showEditValue ? <ViewOffIcon /> : <ViewIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => setShowEditValue(!showEditValue)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <HStack spacing={2}>
                        <Button
                          scheme="primary"
                          size="xs"
                          onClick={() => handleUpdate(v.keyName)}
                          isLoading={envVarMutation.isPending}
                        >
                          Save
                        </Button>
                        <Button
                          scheme="ghost"
                          size="xs"
                          onClick={() => {
                            setEditingVar(null);
                            setEditValue('');
                          }}
                        >
                          Cancel
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <HStack justify="space-between">
                      <HStack spacing={3}>
                        <Text fontSize="small" fontFamily="mono" fontWeight="semibold">
                          {v.keyName}
                        </Text>
                        <Text fontSize="micro" opacity="tertiary">
                          {v.maskedValue}
                        </Text>
                      </HStack>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="Edit variable"
                          icon={<Icon type="PencilEdit" size={14} />}
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            setEditingVar(v.keyName);
                            setEditValue('');
                            setShowEditValue(false);
                          }}
                        />
                        <IconButton
                          aria-label="Delete variable"
                          icon={<Icon type="Trash" size={14} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(v.keyName)}
                        />
                      </HStack>
                    </HStack>
                  )}
                </Box>
              ))}
            </VStack>
          )}

          {/* Usage Example */}
          <Box>
            <Text fontSize="small" fontWeight="semibold" mb={2}>
              Usage Example
            </Text>
            <Box position="relative">
              <Box
                p={3}
                bg="blackAlpha.400"
                borderRadius="md"
                fontFamily="mono"
                fontSize="xs"
                whiteSpace="pre"
                overflowX="auto"
              >
                {ENV_VARS_SNIPPET}
              </Box>
              <IconButton
                aria-label="Copy snippet"
                icon={<Icon type="Copy" size={14} />}
                size="xs"
                variant="ghost"
                position="absolute"
                top={1}
                right={1}
                onClick={() => handleCopyCode(ENV_VARS_SNIPPET)}
              />
            </Box>
          </Box>

          {/* Usage Info */}
          <Box p={3} bg="whiteAlpha.50" borderRadius="md">
            <Text fontSize="micro" opacity="tertiary">
              Variables are encrypted with AES-256-GCM and never exposed in the browser.
              The {'{{VAR_NAME}}'} placeholders are replaced server-side before the request is sent.
              Max 20 variables per entity, values up to 4096 characters.
            </Text>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default EnvVarsEditorTab;
